import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { config } from '../config';
import { identifyUser, clearUserIdentity } from '../utils/observability';
import { clearFilterStorage } from '../components/v4/records/filter';

interface UserData {
  user_id: string;
  username: string;
  name?: string;
  email?: string;
  organization_id: string;
  organization_code: string;
  role: string;
  avatar_url?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: UserData | null;
  token: string | null;
  login: (username: string, password: string, organizationCode: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<UserData | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    // Check if user is authenticated from localStorage
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (storedToken && storedUser) {
      const parsedUser = JSON.parse(storedUser);
      setToken(storedToken);
      setUser(parsedUser);
      setIsAuthenticated(true);

      // Identify user in observability tools (HyperDX)
      identifyUser({
        userId: parsedUser.user_id,
        userEmail: parsedUser.email,
        userName: parsedUser.name || parsedUser.username,
        organizationId: parsedUser.organization_id,
        organizationCode: parsedUser.organization_code,
        userRole: parsedUser.role,
      });
    }

    setLoading(false);
  }, []);
  
  const login = async (username: string, password: string, organizationCode: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${config.API_URL}/v1/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username,
          password,
          organization_code: organizationCode,
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Login failed');
      }
      
      const data = await response.json();
      
      // Store auth data
      const userData: UserData = {
        user_id: data.user_id,
        username: data.username,
        email: data.email,
        organization_id: data.organization_id,
        organization_code: data.organization_code,
        role: data.role,
      };
      
      setUser(userData);
      setToken(data.access_token);
      setIsAuthenticated(true);
      
      // Save to localStorage
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('isAuthenticated', 'true');

      // Identify user in observability tools (HyperDX)
      identifyUser({
        userId: userData.user_id,
        userEmail: userData.email,
        userName: userData.name || userData.username,
        organizationId: userData.organization_id,
        organizationCode: userData.organization_code,
        userRole: userData.role,
      });

      setLoading(false);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      setLoading(false);
      return false;
    }
  };
  
  const logout = () => {
    setIsAuthenticated(false);
    setUser(null);
    setToken(null);

    // Clear localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('isAuthenticated');

    // Clear persisted filter state
    clearFilterStorage();

    // Clear user identity in observability tools (HyperDX)
    clearUserIdentity();
  };
  
  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      user, 
      token, 
      login, 
      logout,
      loading,
      error 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};